import { caaCustomerQuery } from "../graphql/customer-account-gql";
import type { ICaaCustomer, ICaaCustomerQuery, ICaaToken } from "../types/api/customer-account";
import type { TCaaRequestFn } from "../types/graphql";

/**
 * HIKO Social Login Plus(signin-headless) 공통 로직 — New Customer Accounts 스토어에서 hiko 위젯으로 로그인하는 앱용
 * (thisisneverthat jp, yeseyesee kr)
 *
 * 세션/검증 로직만 담당하고 store 접근, 위젯 UI(버튼 스타일), 부팅 시 모달/토스트는 앱이 처리
 * createCaaClient엔 hiko 분기를 넣지 않고 앱이 `refresh` 옵션으로 createHikoSession().refreshCaaToken을 연결
 */

/**
 * signin-headless의 커스텀 엘리먼트 <hiko-signin>
 */
export interface IHikoSigninElement extends HTMLElement {
  isLoggedIn: () => boolean;
  /** provider 없이 `{ method: "email", email }`을 넘기면 브로커가 login hint를 만들어 Shopify OTP 화면으로 바로 보냄 */
  login: (provider?: string | null, opts?: { method?: "email"; email?: string }) => void;
  /**
   * 0.2.0부터 브로커 세션 무효화 후 곧바로 페이지를 리다이렉트(Promise가 정상 resolve 안 되는 게 일반적인 경우)
   * Shopify 세션과 브로커의 first-party 쿠키가 cross-origin fetch로는 안 지워져서 top-level navigation이 필요하다는 게 이유 —
   * id_token이 없는 세션에서만 예외적으로 navigate 없이 resolve 됨
   */
  logout: () => Promise<void>;
  getToken: () => Promise<IHikoToken | null>;
  getSession: () => Promise<{ loggedIn: boolean } | null>;
}

interface INavigationLike {
  addEventListener: (type: "navigate", listener: (event: Event) => void) => void;
  removeEventListener: (type: "navigate", listener: (event: Event) => void) => void;
}

/** 위젯(브로커)이 내려주는 토큰. expiresAt은 ms epoch */
export interface IHikoToken {
  accessToken: string;
  expiresAt: number;
}

/** hiko 위젯으로 발급받은 CAA 토큰 — refresh_token/id_token은 브로커가 보관해 빈 문자열, 갱신/로그아웃은 위젯에 위임 */
export type THikoCaaToken = ICaaToken & { source: "hiko" };

export type THikoLoginResult = { status: "ok"; customer: ICaaCustomer } | { status: "blocked" } | { status: "error" };

/**
 * "blocked"/"error"로 판정되어 위젯 로그아웃이 페이지를 리다이렉트시키기 직전에 기록해두는 sessionStorage 키 ("blocked" | "error")
 * 로그아웃이 Shopify 로그아웃 왕복(top-level navigation)을 거치고 돌아오므로 acceptLogin()의 반환값이 호출부에 전달되지 못할 수 있음 —
 * 앱이 부팅 시 이 값을 먼저 확인해서 모달/토스트를 띄움
 */
export const HIKO_LOGIN_NOTICE_KEY = "hikoLoginNotice";

/** ms epoch expiresAt → ICaaToken.expiresAt(ISO 문자열) 변환 */
export function toHikoCaaToken(token: IHikoToken): THikoCaaToken {
  return {
    accessToken: token.accessToken,
    refreshToken: "",
    idToken: "",
    expiresAt: new Date(token.expiresAt).toISOString(),
    source: "hiko",
  };
}

/** 프로토콜·끝 슬래시를 뺀 myshopify/커스텀 도메인 (<hiko-signin shop> 속성값) */
export function toHikoShopDomain(domain: string): string {
  return domain.replace(/^https?:\/\//, "").replace(/\/$/, "");
}

interface IHikoSessionConfig {
  /** 프로토콜을 뺀 스토어 도메인 (toHikoShopDomain 참고) */
  shopDomain: string;
  /** 갱신된 토큰을 store에 반영 */
  setToken: (token: THikoCaaToken) => void;
  /** 브로커 세션이 끊겨 갱신할 수 없을 때 store 정리 */
  clearToken: () => void;
}

/**
 * 화면에 보이지 않는 <hiko-signin> 싱글턴으로 위젯 세션을 다루는 함수 묶음
 *
 * 로그인 위젯(앱의 SocialLoginCaa)이 언마운트된 뒤에도 CAA 클라이언트의 refresh, 로그아웃에서 위젯 세션에 접근하기 위함
 * 위젯의 세션 토큰은 엘리먼트별 인메모리 상태이고, 리다이렉트-백 URL의 해시(`#hiko_session=...`)를 소비해야만 채워짐 —
 * 이 싱글턴이 로그인 리다이렉트 후 가장 먼저 마운트되는 <hiko-signin>이라 세션을 잡아낼 수 있는 것
 * 풀 리로드 후(소비할 해시가 없으면) 새 엘리먼트는 세션을 복구할 수 없어, caaToken 만료 임박 시 갱신에 실패해 로그아웃될 수 있는 알려진 한계가 있음
 */
export function createHikoSession({ shopDomain, setToken, clearToken }: IHikoSessionConfig) {
  let sessionElement: IHikoSigninElement | null = null;

  const getSessionElement = async (): Promise<IHikoSigninElement> => {
    if (sessionElement) return sessionElement;

    await import("signin-headless");

    const el = document.createElement("hiko-signin") as unknown as IHikoSigninElement;
    el.setAttribute("shop", shopDomain);
    el.style.display = "none";
    document.body.appendChild(el);

    sessionElement = el;
    return el;
  };

  /**
   * 만료 임박/직후 갱신용. 위젯이 브로커 세션(refresh_token은 Hiko가 서버사이드로 보관)을 통해 새 access_token을 내려줌
   * 세션 자체가 끊긴 경우(getToken이 값을 못 주는 경우) 로그아웃 처리
   */
  const refreshCaaToken = async (): Promise<THikoCaaToken | null> => {
    const el = await getSessionElement();

    const token = await el.getToken().catch((error: unknown) => {
      if (error instanceof Error && error.message === "unauthorized") return null;
      throw error;
    });

    if (!token?.accessToken) {
      clearToken();
      return null;
    }

    const caaToken = toHikoCaaToken(token);
    setToken(caaToken);
    return caaToken;
  };

  /**
   * 위젯 로그아웃. Shopify 로그아웃 페이지로 이동이 시작됐으면 true
   *
   * el.logout()은 내부에서 location.assign()으로 이동을 "예약"만 하고 곧바로 resolve 되므로,
   * resolve 됐다고 이동이 없었다고 보면 안 됨 — 호출부가 SPA 홈 이동을 한 직후 실제 이동이 일어나 페이지가 두 번 바뀜
   * Navigation API(navigate 이벤트) / beforeunload로 다른 문서로의 이동 시작을 감지하고,
   * 감지 수단이 없는 브라우저를 위해 redirectTimeoutMs 동안 페이지가 떠나는지 기다림 (0이면 기다리지 않음)
   */
  const logout = async ({ redirectTimeoutMs = 0 }: { redirectTimeoutMs?: number } = {}): Promise<boolean> => {
    const el = await getSessionElement();
    const currentUrl = window.location.href;

    // 위젯은 로그아웃 후 돌아올 주소(post_logout_redirect_uri)로 현재 location.href를 그대로 넘기는데,
    // Shopify는 Customer Account API 클라이언트에 등록된 Logout URI와 경로까지 일치해야 받아줌 —
    // 경로가 붙은 페이지(/account/... 등)에서 로그아웃하면 거부되어 prod url로 이동됨
    // 호출 직전에 URL을 사이트 루트로 바꿔 넘김
    window.history.replaceState(window.history.state, "", "/");

    // replaceState(같은 문서)는 제외하려고 리스너는 그 뒤에 등록하고 sameDocument도 확인
    let isLeaving = false;
    const navigation = (window as unknown as { navigation?: INavigationLike }).navigation;
    const onNavigate = (event: Event) => {
      if (!(event as unknown as { destination?: { sameDocument?: boolean } }).destination?.sameDocument) isLeaving = true;
    };
    const onBeforeUnload = () => {
      isLeaving = true;
    };
    navigation?.addEventListener("navigate", onNavigate);
    window.addEventListener("beforeunload", onBeforeUnload);

    try {
      await el.logout();
      if (!isLeaving && redirectTimeoutMs > 0) await new Promise((resolve) => setTimeout(resolve, redirectTimeoutMs));
      return isLeaving;
    } catch (error) {
      window.history.replaceState(window.history.state, "", currentUrl);
      throw error;
    } finally {
      navigation?.removeEventListener("navigate", onNavigate);
      window.removeEventListener("beforeunload", onBeforeUnload);
    }
  };

  /**
   * 위젯의 브로커 세션에 로그인된 토큰이 있으면 읽기만 함(store에 반영하지 않는 순수 조회)
   * 계정 검증(탈퇴요청 등) 전에 store를 먼저 오염시키지 않기 위해 acceptLogin과 분리
   */
  const getSessionToken = async (): Promise<IHikoToken | null> => {
    const el = await getSessionElement();
    if (!el.isLoggedIn()) return null;
    return el.getToken();
  };

  /**
   * 위젯은 로그인 후 돌아올 주소로 호출 시점의 location.href를 그대로 넘김(<hiko-signin>에 return 주소 속성이 없음)
   * returnPath가 있으면 호출 직전에 URL을 바꿔서 로그인 후 그 경로로 바로 돌아오게 함
   * (예: 체크아웃 진입 시도 → 로그인 → 로그인을 시작한 페이지를 거치지 않고 /checkout으로 복귀)
   */
  const loginWithReturnPath = (returnPath: string | null | undefined, login: () => void) => {
    const currentUrl = window.location.href;
    const shouldReplace = Boolean(returnPath) && returnPath !== window.location.pathname;

    if (shouldReplace) {
      window.history.replaceState(window.history.state, "", returnPath);

      // 소셜/OTP 페이지에서 뒤로 가기로 bfcache 복원되면 URL만 바뀐 채 남으므로 원래 주소로 되돌림
      const handlePageShow = (event: PageTransitionEvent) => {
        if (event.persisted) window.history.replaceState(window.history.state, "", currentUrl);
      };
      window.addEventListener("pageshow", handlePageShow, { once: true });
    }

    try {
      login();
    } catch (error) {
      if (shouldReplace) window.history.replaceState(window.history.state, "", currentUrl);
      throw error;
    }
  };

  /** 이메일(OTP) 로그인 시작 — 위젯의 이메일 폼 대신 앱의 자체 이메일 입력 UI에서 사용 (풀 리다이렉트) */
  const startEmailLogin = async (email: string, { returnPath }: { returnPath?: string | null } = {}): Promise<void> => {
    const el = await getSessionElement();
    loginWithReturnPath(returnPath, () => {
      el.login(null, { method: "email", email });
    });
  };

  /** 소셜 로그인 시작 — 위젯의 소셜 버튼 대신 앱의 자체 버튼에서 사용 (풀 리다이렉트) */
  const startSocialLogin = async (provider: string, { returnPath }: { returnPath?: string | null } = {}): Promise<void> => {
    const el = await getSessionElement();
    loginWithReturnPath(returnPath, () => {
      el.login(provider);
    });
  };

  return { refreshCaaToken, logout, getSessionToken, startEmailLogin, startSocialLogin };
}

export type THikoSession = ReturnType<typeof createHikoSession>;

interface IHikoAuthSyncConfig {
  session: THikoSession;
  /** 앱의 CAA GraphQL 실행기 (createCaaClient().request) */
  request: TCaaRequestFn;
  /** 현재 store의 caaToken — 부팅 시 유효한 토큰이 이미 있으면 동기화를 건너뜀 */
  getToken: () => ICaaToken | null;
  setToken: (token: THikoCaaToken) => void;
  clearToken: () => void;
  setCustomer: (customer: ICaaCustomer) => void;
  /** 로그인을 막을 고객 태그 (기본 "탈퇴요청") */
  blockedTag?: string;
  /** 검증 통과 후 앱별 추가 처리 (예: tnt jp의 로그인 provider 기록) */
  onAccepted?: (customer: ICaaCustomer) => void;
}

/**
 * hiko 로그인 반영(계정 검증 → store 반영) 함수 묶음
 * createHikoSession과 분리 — CAA 클라이언트(refresh)가 session을, 이 쪽은 CAA 클라이언트(request)를 필요로 해서 한 모듈에 두면 순환 참조가 됨
 */
export function createHikoAuthSync({
  session,
  request,
  getToken,
  setToken,
  clearToken,
  setCustomer,
  blockedTag = "탈퇴요청",
  onAccepted,
}: IHikoAuthSyncConfig) {
  /**
   * 위젯에서 받은 access_token을 store에 반영하기 전에 계정 상태(탈퇴요청 등)를 검증
   *
   * "error"/"blocked"에선 로컬 store 로그아웃뿐 아니라 브로커 로그아웃도 바로 처리 —
   * 미뤄두면(예: 모달 확인 시점까지) 브로커 세션이 살아있는 채로 남아 다음 로그인 시도(다른 provider 포함)와 꼬일 수 있음
   *
   * 브로커 로그아웃은 Shopify 로그아웃 페이지로 곧바로 리다이렉트하므로 아래 `return`문까지 도달하지 못할 수 있음 —
   * 리다이렉트 전에 HIKO_LOGIN_NOTICE_KEY를 남겨두고, 돌아온 페이지(앱 부팅 시점)에서 모달/토스트를 띄운다
   */
  const acceptLogin = async (token: IHikoToken): Promise<THikoLoginResult> => {
    setToken(toHikoCaaToken(token));

    const data = await request<ICaaCustomerQuery>(caaCustomerQuery).catch(() => null);

    if (!data?.customer || data.customer.tags.includes(blockedTag)) {
      const notice = data?.customer ? "blocked" : "error";

      clearToken();
      sessionStorage.setItem(HIKO_LOGIN_NOTICE_KEY, notice);
      try {
        await session.logout();
      } catch {
        // 리다이렉트 전 네트워크 실패 등 — 로컬 로그아웃은 이미 처리됨
      }
      return { status: notice };
    }

    setCustomer(data.customer);
    onAccepted?.(data.customer);

    return { status: "ok", customer: data.customer };
  };

  /**
   * 앱 부팅 시 1회 호출. 소셜/이메일 로그인 모두 Shopify 호스티드 페이지를 거치는 풀 리다이렉트라
   * 리다이렉트-백 시점엔 로그인 드로어가 이미 언마운트되어 `hiko:login` 이벤트를 아무도 못 받을 수 있음 —
   * 위젯의 브로커 세션을 직접 확인해 동기화. 이미 유효한 caaToken이 있으면 건드리지 않음
   *
   * 결과를 그대로 반환 — 모달/토스트 등 UI는 호출부(App.tsx)가 처리
   */
  const syncOnBoot = async (): Promise<THikoLoginResult | null> => {
    const caaToken = getToken();
    if (caaToken?.accessToken && new Date(caaToken.expiresAt).getTime() > Date.now()) return null;

    const token = await session.getSessionToken();
    if (!token) return null;

    return acceptLogin(token);
  };

  return { acceptLogin, syncOnBoot };
}
